import { useState, useEffect } from 'react'
import useMenuBack from '../../hooks/useMenuBack'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Shield, Smartphone, MessageSquare, KeyRound, Copy, Check, AlertCircle } from 'lucide-react'
import { authAPI } from '../../services/api'
import { ResponsiveWrapper } from '../../components/layout'
import { useI18n } from '../../i18n'
import { apiErrorMessage } from '../../utils/apiErrorMessage'
import CurrentFactorField, { LockNotice, useLockCountdown } from '../../components/CurrentFactorField'
import { currentCodeProblem, currentCodeMayBeSpent, currentFactorRefusal, normalizeCurrentCode, refusalSentence } from '../../utils/currentFactor'
import { OrellaSpinner } from '../../components/OrellaLoader' // s123: the app's one spinner

export default function TwoFactorManageScreen() {
  const navigate = useNavigate()
  const goBack = useMenuBack()
  
  const { t } = useI18n()
  
  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(true)
  const [regenCodes, setRegenCodes] = useState(null)
  const [regenLoading, setRegenLoading] = useState(false)
  const [copiedCodes, setCopiedCodes] = useState(false)

  useEffect(() => {
    const loadStatus = async () => {
      try {
        const data = await authAPI.get2faStatus()
        setStatus(data)
      } catch (err) {
        console.error('[2FA] Failed to load status:', err)
      } finally {
        setLoading(false)
      }
    }
    loadStatus()
  }, [])

  // PROD-1154: 2FA is on here, so new backup codes need a CURRENT code first (utils/currentFactor.js).
  const [regenAsk, setRegenAsk] = useState(false)
  const [regenCurrentCode, setRegenCurrentCode] = useState('')
  const [regenError, setRegenError] = useState('')
  const [regenLockSeconds, startRegenLock] = useLockCountdown()

  // PROD-1154 (review M1): the first tap asks only "Continue?" and regenerates WITHOUT a code, as before. The
  //   field opens only when the API asks (403 2fa_current_factor_required, nothing used up); the live API
  //   (v243.369.0) never asks, so there the new codes come at once.
  const handleRegenerate = async () => {
    if (!confirm('This will invalidate all your existing backup codes and generate new ones. Continue?')) return
    setRegenCurrentCode('')
    await runRegenerate(undefined)
  }

  const cancelRegenerate = () => {
    setRegenAsk(false)
    setRegenCurrentCode('')
    setRegenError('')
  }

  // PROD-1154: once the API has asked, the typed current code goes out; nothing is sent without one that can be one.
  const submitRegenerate = async () => {
    // Review L-1: an empty field gets no red line; the field's own grey line already says a code is needed.
    const problem = currentCodeProblem(true, regenCurrentCode)
    if (problem) { setRegenError(refusalSentence(t, problem)); return }
    await runRegenerate(normalizeCurrentCode(regenCurrentCode))
  }

  // PROD-1154: the one regenerate call, with or without a current code. Refusals by `code`; the lockout counts down;
  //   a code that may have been used up is emptied (review L1), so a retry cannot spend an attempt on it.
  const runRegenerate = async (sentCode) => {
    setRegenError('')
    setRegenLoading(true)
    try {
      const response = await authAPI.regenerateBackupCodes(sentCode)
      setRegenCodes(response.backupCodes)
      setStatus(prev => prev ? { ...prev, backupCodesRemaining: 10 } : prev)
      setRegenAsk(false)
      setRegenCurrentCode('')
    } catch (err) {
      if (sentCode && currentCodeMayBeSpent(err)) setRegenCurrentCode('')
      const refused = currentFactorRefusal(err)
      if (refused && (refused.ask || refused.locked)) setRegenAsk(true)
      if (refused?.locked) startRegenLock(refused.seconds)
      else if (refused) setRegenError(refusalSentence(t, refused.code))
      else setRegenError(apiErrorMessage(err, t('errors2.failedToRegenerateCodes')))
    } finally {
      setRegenLoading(false)
    }
  }

  const handleCopyCodes = () => {
    if (!regenCodes) return
    navigator.clipboard.writeText(regenCodes.join('\n')).then(() => {
      setCopiedCodes(true)
      setTimeout(() => setCopiedCodes(false), 3000)
    })
  }

  const handleChangeMethod = () => {
    navigate('/security', { state: { resetup: true, currentMethod: status?.method } })
  }

  return (
    <ResponsiveWrapper className="bg-white shadow-xl">
    <div className="screen-min-h bg-white flex flex-col">
      {/* Header */}
      <div className="sticky-page-header px-4 pt-4 pb-2 flex items-center gap-3">
        <button onClick={() => goBack()} className="p-2 -ml-2 text-gray-600">
          <ArrowLeft size={24} />
        </button>
        <h1 className="text-xl font-bold text-orella-700">{t('security.twofactorAuthentication')}</h1>
      </div>

      {/* Content */}
      <div className="flex-1 px-4 pb-8">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <OrellaSpinner size={32} className="mb-3" />
            <p className="text-sm text-gray-500">{t('security.loadingSecuritySettings')}</p>
          </div>
        ) : (
          <>
            {/* Status badge */}
            <div className="flex items-center gap-3 mb-6 mt-2">
              <div className="w-12 h-12 rounded-full bg-orella-50 flex items-center justify-center">
                <Shield className="w-6 h-6 text-orella-500" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-gray-800">{t('security.accountProtection')}</p>
                <p className="text-sm text-gray-500">{t('security.requiredForYourHealthData')}</p>
              </div>
              {status?.enabled ? (
                <span className="text-xs font-medium text-green-700 bg-green-100 px-3 py-1 rounded-full">{t('security.active')}</span>
              ) : (
                <span className="text-xs font-medium text-amber-700 bg-amber-100 px-3 py-1 rounded-full">{t('security.notSetUp')}</span>
              )}
            </div>

            {status?.enabled ? (
              <>
                {/* Current method */}
                <div className="mb-3">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">{t('security.currentMethod')}</p>
                  <div className="bg-gray-50 rounded-2xl p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-orella-500 flex items-center justify-center">
                          {status.method === 'sms' 
                            ? <MessageSquare className="w-5 h-5 text-white" />
                            : <Smartphone className="w-5 h-5 text-white" />
                          }
                        </div>
                        <div>
                          <p className="font-medium text-gray-800">
                            {status.method === 'sms' ? 'Text message (SMS)' : 'Authenticator app'}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {status.method === 'sms' 
                              ? 'Code sent via text each sign-in'
                              : 'Google Authenticator, Authy, etc.'}
                          </p>
                        </div>
                      </div>
                    </div>
                    <button 
                      onClick={handleChangeMethod}
                      className="mt-3 text-sm font-medium text-orella-500"
                    >
                      {t('security.changeVerificationMethod')}
                    </button>
                  </div>
                </div>

                {/* Backup codes */}
                <div className="mb-3">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">{t('security.backupCodes')}</p>
                  <div className="bg-gray-50 rounded-2xl p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-xl bg-white border border-gray-200 flex items-center justify-center">
                        <KeyRound className="w-5 h-5 text-gray-500" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-800">{t('security.emergencyAccessCodes')}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full rounded-full transition-all"
                              style={{ 
                                width: `${((status.backupCodesRemaining || 0) / 10) * 100}%`,
                                backgroundColor: (status.backupCodesRemaining || 0) <= 3 ? '#EF9F27' : '#66D170'
                              }}
                            />
                          </div>
                          <span className="text-xs text-gray-500">
                            {status.backupCodesRemaining || 0} of 10 remaining
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Low codes warning */}
                    {status.backupCodesRemaining <= 3 && status.backupCodesRemaining > 0 && !regenCodes && (
                      <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                        <span className="text-xs text-amber-700">{t('security.runningLowOnBackupCodes')}</span>
                      </div>
                    )}

                    {/* PROD-1154: a failure shows whether or not the API has asked for a code yet */}
                    {!regenLockSeconds && regenError && (
                      <div role="alert" className="mb-3 p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-600">{regenError}</div>
                    )}

                    {/* PROD-1154: a CURRENT code, once the API has asked for one (403 required) */}
                    {regenAsk && (
                      <div className="mb-3">
                        <LockNotice seconds={regenLockSeconds} />
                        <CurrentFactorField
                          value={regenCurrentCode}
                          onChange={(v) => { setRegenCurrentCode(v); setRegenError('') }}
                          method={status.method}
                          disabled={regenLoading || !!regenLockSeconds}
                          autoFocus
                          onEnter={submitRegenerate}
                        />
                      </div>
                    )}

                    <button 
                      onClick={regenAsk ? submitRegenerate : handleRegenerate}
                      disabled={regenLoading || (regenAsk && !!regenLockSeconds)}
                      className="w-full py-3 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 disabled:opacity-50"
                    >
                      {regenLoading ? 'Generating new codes...' : 'Regenerate backup codes'}
                    </button>
                    {regenAsk && (
                      <button
                        type="button"
                        onClick={cancelRegenerate}
                        disabled={regenLoading}
                        className="w-full mt-2 py-2 text-sm font-medium text-gray-500 disabled:opacity-50"
                      >
                        {t('common.cancel', 'Cancel')}
                      </button>
                    )}
                  </div>
                </div>

                {/* Regenerated codes display */}
                {regenCodes && (
                  <div className="mb-3 bg-white border border-gray-200 rounded-2xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-gray-800">{t('security.newBackupCodes')}</h3>
                      <button onClick={handleCopyCodes} className="flex items-center gap-1.5 text-xs font-medium text-orella-500">
                        {copiedCodes ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                        {copiedCodes ? 'Copied!' : 'Copy all'}
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      {regenCodes.map((code, i) => (
                        <div key={i} className="font-mono text-sm text-gray-700 bg-gray-50 px-3 py-2 rounded-lg text-center border border-gray-100">
                          {code}
                        </div>
                      ))}
                    </div>
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
                      <p className="text-xs text-amber-700">
                        {t('security.newBackupCodesBody', "Save these codes somewhere safe. Your previous codes have been invalidated. You won't see these again after leaving this screen.")}
                      </p>
                    </div>
                    <button 
                      onClick={() => setRegenCodes(null)}
                      className="mt-3 w-full py-3 bg-orella-500 text-white rounded-full font-semibold text-sm"
                    >
                      I've saved my codes
                    </button>
                  </div>
                )}

                {/* Info note */}
                <p className="text-xs text-gray-400 mt-4 text-center px-4">
                  {t('security.twoFactorCannotBeDisabled', 'Two-factor authentication protects your health information and cannot be disabled.')}
                </p>
              </>
            ) : (
              /* Not set up */
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-orella-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-orella-500" />
                </div>
                <p className="font-semibold text-gray-800 mb-2">{t('security.twofactorAuthenticationNotSetUp')}</p>
                <p className="text-sm text-gray-500 mb-6">{t('security.addAnExtraLayerOf')}</p>
                <button 
                  onClick={() => navigate('/security', { replace: true })}
                  className="px-8 py-3 bg-orella-500 text-white rounded-full font-semibold"
                >
                  {t('security.setUp2fa')}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
    </ResponsiveWrapper>
  )
}
